import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The shared furniture of BOTH registers — canvas `1u`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SET OF COMPONENTS, TWO SCREENS. NOT TWO NEAR-COPIES.
 * ─────────────────────────────────────────────────────────────────────────────
 * Devices and SIMs are the same screen with different columns: a count line, a
 * search box, a row of filter chips, a table, and a per-row action strip. Built
 * twice they drift — one gains an empty state the other lacks, one gets the
 * chip spacing right — and every later change has to be made in both places and
 * usually is not. `RegisterScreen` is the shape; `Devices.tsx` and `Sims.tsx`
 * are configuration.
 *
 * WHAT COMES FROM THE ARTBOARD. `1u` draws the count line ("118 active · 17 in
 * stock"), the chip row (Active / Stock / Offline / Expiring SIMs), a seven
 * column table and an Actions column. Two of its chips are not built: "Offline"
 * needs last-position state that the register does not carry, and "Expiring
 * SIMs" needs a carrier expiry date that no column holds. Neither ships greyed
 * out. `1u` also draws a Traccar-ingest / SIM-fleets / server-health strip and a
 * "Batch import CSV" button; none of the three has data or an endpoint behind
 * it, so none of them is here.
 *
 * There is NO ARTBOARD AT ALL for the SIM register as its own screen, for the
 * assignment-history drawer, or for any of these dialogs — `1u` mixes devices
 * and SIMs into one table and draws no detail view. Those surfaces match the
 * shipped admin screens (Tenants, Plans) rather than an approximated pixel
 * source, and that is stated rather than implied.
 */

// Slice 84b — `min-h-11` (44px) on a phone for a comfortable tap target, back to
// the desktop `min-h-10` from `sm`, so desktop renders byte-identically.
export const INPUT =
  'w-full min-h-11 sm:min-h-10 rounded-lg border border-raad-border bg-raad-input px-3 text-[13.5px] text-raad-text outline-none focus:border-raad-primary';

/**
 * 🔴 THE HINT LIVES OUTSIDE THE `<label>`, DELIBERATELY.
 *
 * Nested inside, it becomes part of the control's accessible name — the field
 * for "ICCID" is announced as "ICCID The long number printed on the SIM…" and
 * can no longer be found by its own name, by a screen reader or by a test. The
 * provisioning wizard earned this the hard way.
 */
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-[12px] font-medium text-raad-muted">{label}</span>
        {children}
      </label>
      {hint ? <span className="mt-1 block text-[11.5px] text-raad-muted">{hint}</span> : null}
    </div>
  );
}

/** The modal shell every inventory dialog sits in. Matches `EditPlanDialog`. */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      {/* Slice 84b — a full-height bottom-anchored SHEET below `sm` (min-h-dvh,
          square, edge-to-edge), the pre-84b centred card from `sm`. The class
          delta is identical across all 15 dialog shells. */}
      <div
        className={[
          'w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6',
          wide ? 'sm:max-w-2xl' : 'sm:max-w-md',
        ].join(' ')}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 sm:min-h-9 shrink-0 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('inventory.close')}
          </button>
        </div>
        {children}
        {footer ? <div className="mt-5 flex items-center justify-end gap-3">{footer}</div> : null}
      </div>
    </div>
  );
}

/** A refusal, said in words. `errorKey` comes from `inventoryErrorKey`. */
export function ErrorNote({ errorKey }: { errorKey: string | null }) {
  const { t } = useTranslation();
  if (errorKey === null) return null;
  return (
    <p
      role="alert"
      className="mt-3 rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
    >
      {t(errorKey)}
    </p>
  );
}

/**
 * A plain-language caution, not a decoration.
 *
 * Used for the one operational control this slice cannot enforce in code: the
 * trip detector resolves device → vehicle through the CURRENT mapping, so
 * re-detection across a tracker move would attribute one vehicle's journeys to
 * another. If the warning is not on the screen it does not exist.
 */
export function Caution({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px] text-raad-text">
      {children}
    </p>
  );
}

// Slice 84b — 44px+ tap targets on a phone (`min-h-12`/`min-h-11`), back to the
// desktop sizes from `sm` so desktop is byte-identical.
export const PRIMARY_BUTTON =
  'min-h-12 sm:min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40';
export const SECONDARY_BUTTON =
  'min-h-11 sm:min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold transition-colors hover:bg-raad-primary/10 disabled:opacity-40';

/** A status word in a bordered pill. Tone is a token class, never a literal. */
export function Pill({ label, tone }: { label: string; tone: string }) {
  return (
    <span
      className={[
        'inline-flex rounded-md border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide',
        tone,
      ].join(' ')}
    >
      {label}
    </span>
  );
}

export interface RegisterColumn<T> {
  key: string;
  label: string;
  /** The cell. Anything, including the row's action buttons. */
  render: (row: T) => ReactNode;
  align?: 'right';
}

export interface RegisterChip<T> {
  key: string;
  label: string;
  predicate: (row: T) => boolean;
}

/**
 * The list screen shared by both registers.
 *
 * Search and chips are ANDed and both are client-side: the whole register is a
 * few hundred rows of Raad's own hardware, it is fetched once, and a server-side
 * filter endpoint for it would be a second place for "in stock" to be defined.
 */
export function RegisterScreen<T extends { id: string }>({
  headingId,
  title,
  headline,
  headerAction,
  rows,
  columns,
  chips,
  searchLabel,
  matches,
  emptyText,
  noMatchText,
  children,
}: {
  headingId: string;
  title: string;
  headline: ReactNode;
  headerAction?: ReactNode;
  rows: T[];
  columns: Array<RegisterColumn<T>>;
  chips: Array<RegisterChip<T>>;
  searchLabel: string;
  matches: (row: T, needle: string) => boolean;
  emptyText: string;
  noMatchText: (q: string) => string;
  /** Dialogs and drawers, rendered by the owning screen. */
  children?: ReactNode;
}) {
  const [search, setSearch] = useState('');
  const [chipKey, setChipKey] = useState<string>(chips[0]?.key ?? 'all');

  const chip = chips.find((c) => c.key === chipKey) ?? chips[0];
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter(
    (r) => (chip === undefined || chip.predicate(r)) && (needle === '' || matches(r, needle)),
  );

  return (
    <section aria-labelledby={headingId}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id={headingId} className="font-display text-xl font-bold tracking-tight">
          {title}
        </h1>
        {headerAction ? <div className="order-last ms-auto">{headerAction}</div> : null}
        <p className="text-sm text-raad-muted">{headline}</p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="block max-w-xs grow">
          <span className="sr-only">{searchLabel}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchLabel}
            className="min-h-11 sm:min-h-10 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={c.key === chipKey}
              onClick={() => setChipKey(c.key)}
              className={[
                'min-h-11 sm:min-h-9 rounded-full border px-3 text-[12px] font-semibold transition-colors',
                c.key === chipKey
                  ? 'border-raad-primary/40 bg-raad-primary/15 text-raad-accent'
                  : 'border-raad-border text-raad-muted hover:text-raad-text',
              ].join(' ')}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{emptyText}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{noMatchText(search)}</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={[
                      'py-2.5 pr-4 font-semibold',
                      c.align === 'right' ? 'text-right' : 'text-left',
                    ].join(' ')}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-b border-raad-border last:border-b-0">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={[
                        'py-3 pr-4 align-top',
                        c.align === 'right' ? 'text-right' : '',
                      ].join(' ')}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * `<input type="datetime-local">` → an ISO-8601 instant, or `undefined`.
 *
 * The wire wants `z.string().datetime()`, which is UTC with an offset; the input
 * hands back a naive local string with no zone at all. `new Date(local)` reads
 * it in the BROWSER's zone — which is the zone the technician typed it in — and
 * `toISOString()` states it absolutely. Blank means "now", which the server
 * fills in, so the field is never sent empty.
 */
export function toIsoInstant(local: string): string | undefined {
  const trimmed = local.trim();
  if (trimmed === '') return undefined;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

/**
 * An ISO instant, in the reader's own locale and zone.
 *
 * `Intl` is given the active i18n language so a Somali session does not read
 * dates in English. It is NOT given a fixed timezone: an assignment history is
 * read by whoever is looking at it, and the browser's zone is the one they think
 * in. The value on the wire is UTC and stays UTC.
 */
export function formatInstant(iso: string, language: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

/** The `at` + `note` pair every assignment write accepts. */
export function WhenAndNote({
  at,
  note,
  onAt,
  onNote,
}: {
  at: string;
  note: string;
  onAt: (v: string) => void;
  onNote: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t('inventory.when')} hint={t('inventory.whenHint')}>
        <input
          className={INPUT}
          type="datetime-local"
          value={at}
          onChange={(e) => onAt(e.target.value)}
        />
      </Field>
      <Field label={t('inventory.note')}>
        <input className={INPUT} value={note} onChange={(e) => onNote(e.target.value)} />
      </Field>
    </>
  );
}
